import {
  apply, cloneSeat, hydrateSeat, validateGrid,
  type Dictionary, type GridReport, type MatchState, type Seat,
  type WireSeat, type WireState, type Action,
} from '@nt2/engine';
import type { ConnectionStatus, Transport } from '../net/transport.js';

/**
 * The client's view of the match.
 *
 * Deliberately a thin cache over whatever the authority last said, plus a
 * locally-computed validity report. Moving your own tiles is shown at once
 * (see {@link GameStore.send}) and then simply overwritten by the next
 * authoritative state.
 */
export interface Store {
  wire: WireState | null;
  status: ConnectionStatus;
  dict: Dictionary;
  /** Hydrated form of the seat this client plays. */
  seat: Seat | null;
  report: GridReport | null;
  /** Seat currently being watched in the spectate panel. */
  watching: string | null;
  error: string | null;
}

type Listener = () => void;

/** Moves of your own tiles: safe to show before the server answers. */
const PREDICTABLE: ReadonlySet<Action['t']> = new Set<Action['t']>([
  'place', 'recall', 'moveGroup', 'reorderRack', 'recallAll', 'arrange',
]);

export class GameStore {
  #s: Store;
  #listeners = new Set<Listener>();
  #transport: Transport | null = null;

  constructor(dict: Dictionary) {
    this.#s = {
      wire: null, status: 'connecting', dict,
      seat: null, report: null, watching: null, error: null,
    };
  }

  get state(): Readonly<Store> { return this.#s; }

  subscribe(fn: Listener): () => void {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  #emit(): void { for (const fn of this.#listeners) fn(); }

  attach(transport: Transport): void {
    this.detach();
    this.#transport = transport;
  }

  /**
   * Close the transport and forget it.
   *
   * Closing alone was not enough: the reference survived, so anything still
   * holding the store could send an action into a match that had been left
   * and get its state pushed back.
   */
  detach(): void {
    this.#transport?.close();
    this.#transport = null;
  }

  get transport(): Transport | null { return this.#transport; }

  /**
   * Send an action — and, for moving your own tiles, show it straight away.
   *
   * Online, a dropped letter used to sit in limbo until the server answered:
   * a round trip, which from a phone or a distant region is easily a few
   * hundred milliseconds, and reads as the drag not having worked. So moves of
   * your own tiles are applied at once to a private copy of your seat, by the
   * same engine the server runs — same rules, same refusals — and the server's
   * reply then confirms it or quietly puts it right.
   *
   * Moves not yet answered are replayed on top of each state that arrives, so
   * a reply to the *first* of two quick moves does not snap the second one
   * back. Every predictable action is idempotent, which is what makes the
   * replay safe; anything else (a call, a dump, a check) waits for the server.
   */
  send(action: Action): void {
    this.#transport?.send(action);
    if (this.#transport?.kind !== 'remote' || !PREDICTABLE.has(action.t)) return;
    this.#pending.push({ action, at: Date.now() });
    const next = this.#predict(this.#s.seat, [action]);
    if (next) {
      this.#s.seat = next;
      this.recompute();
    }
  }

  #pending: Array<{ action: Action; at: number }> = [];

  /** Apply actions to a copy of a seat with the real engine, or null. */
  #predict(seat: Seat | null, actions: Action[]): Seat | null {
    const wire = this.#s.wire;
    if (!seat || !wire || wire.phase !== 'playing') return null;
    const copy = cloneSeat(seat);
    // Just enough of a match for the tile-moving rules, which only ever touch
    // the one seat they are given.
    const shadow = {
      matchId: wire.matchId, phase: 'playing', config: wire.config,
      seats: new Map([[copy.id, copy]]), players: new Map(), bag: [], leftover: [],
      log: [], version: 0, roundsTaken: 0, startedAt: wire.startedAt,
      finishedAt: null, winnerSeatId: null, wentOutSeatId: null, lastDrawAt: 0,
      seatSeq: 0,
    } as unknown as MatchState;
    let changed = false;
    for (const a of actions) {
      try {
        if (apply(shadow, a, this.#s.dict, Date.now()).ok) changed = true;
      } catch { /* a prediction is a courtesy; the server's word stands */ }
    }
    return changed ? copy : null;
  }

  setDictionary(dict: Dictionary): void {
    this.#s.dict = dict;
    this.recompute();
  }

  setStatus(status: ConnectionStatus): void { this.#s.status = status; this.#emit(); }

  setError(error: string | null): void { this.#s.error = error; this.#emit(); }

  watch(seatId: string | null): void { this.#s.watching = seatId; this.#emit(); }

  applyState(wire: WireState): void {
    this.#s.wire = wire;
    const mine = wire.seats.find((x) => x.id === wire.youSeatId) ?? null;
    let seat = mine ? hydrateSeat(mine) : null;
    // Replay what the server has probably not seen yet. Two seconds is far
    // longer than any answer takes; past that a move has either landed or
    // been refused, and the server's version is the truth.
    const now = Date.now();
    this.#pending = this.#pending.filter((p) => now - p.at < 2000);
    if (seat && this.#pending.length) {
      seat = this.#predict(seat, this.#pending.map((p) => p.action)) ?? seat;
    }
    this.#s.seat = seat;
    this.recompute();
  }

  /**
   * Recompute the validity report for our own grid.
   *
   * Always computed, never hidden here — what the *player* is allowed to see
   * of it is a presentation decision made in the board, driven by the match's
   * peek policy. Keeping the full report in the store lets the take-two button
   * know whether it is worth pulsing without leaking which word is wrong.
   */
  recompute(): void {
    const wire = this.#s.wire;
    const seat = this.#s.seat;
    this.#s.report = seat && wire
      ? validateGrid(seat.grid, this.#s.dict, {
          minWordLength: wire.config.minWordLength, decide: true,
        })
      : null;
    this.#emit();
  }

  // ---- convenience selectors -------------------------------------------
  get wire(): WireState | null { return this.#s.wire; }
  get seat(): Seat | null { return this.#s.seat; }
  get me() {
    const w = this.#s.wire;
    return w ? w.players.find((p) => p.seatId === w.youSeatId && !p.isSpectator) ?? null : null;
  }
  get isHost(): boolean {
    const w = this.#s.wire;
    if (!w) return false;
    return w.players.some((p) => p.isHost && p.seatId === w.youSeatId);
  }
  seatById(id: string): WireSeat | null {
    return this.#s.wire?.seats.find((x) => x.id === id) ?? null;
  }
  /** True when the rack is empty and the grid is legal. */
  get canCall(): boolean {
    const seat = this.#s.seat;
    const rep = this.#s.report;
    return !!seat && seat.rack.length === 0 && !!rep?.complete;
  }
}
